import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { CATEGORIES, type Category, type Place } from '@map/shared';
import { ApiError, photoUrl, thumbUrl } from '../api.js';
import { formatClock } from '../format.js';
import { categoryStyle, scoreLabel, scoreTone } from '../map/markers.js';
import { useStore } from '../store/StoreContext.js';
import { commentsOf, memberById, myVote, photosOf, voteScore } from '../store/selectors.js';

export interface PlacePanelProps {
  readonly place: Place;
  readonly onClose: () => void;
}

/** 8 Mo : la meme limite que le serveur, refusee ici pour eviter un envoi inutile. */
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function PlacePanel({ place, onClose }: PlacePanelProps): ReactNode {
  const store = useStore();
  const { state, me } = store;

  const [title, setTitle] = useState(place.title);
  const [description, setDescription] = useState(place.description);
  const [category, setCategory] = useState<Category>(place.category);
  const [url, setUrl] = useState(place.url ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draftComment, setDraftComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const [uploadRatio, setUploadRatio] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Quand un autre participant modifie le lieu affiche, on recharge les champs
  // depuis le state. On ne le fait qu'au changement de lieu ou d'`updatedAt`
  // pour ne pas ecraser une saisie en cours a chaque rendu.
  useEffect(() => {
    setTitle(place.title);
    setDescription(place.description);
    setCategory(place.category);
    setUrl(place.url ?? '');
    setError(null);
  }, [place.id, place.updatedAt, place.title, place.description, place.category, place.url]);

  const score = voteScore(state, place.id);
  const mine = myVote(state, place.id, me?.id ?? null);
  const author = memberById(state, place.authorId);
  const comments = commentsOf(state, place.id);
  const photos = photosOf(state, place.id);
  const isStep = place.status === 'itinerary';

  /**
   * Un 404 signifie que quelqu'un a supprime le lieu pendant qu'on l'editait.
   * On ferme la fiche et on le dit : garder un formulaire ouvert sur un lieu
   * fantome ferait echouer chaque enregistrement suivant sans explication.
   */
  function handleFailure(caught: unknown, fallback: string): void {
    if (caught instanceof ApiError && caught.status === 404) {
      store.setBanner('Ce lieu vient d etre supprime par quelqu un d autre.');
      onClose();
      return;
    }
    if (caught instanceof ApiError && caught.status === 400) {
      setError(caught.message);
      return;
    }
    setError(fallback);
  }

  const dirty =
    title !== place.title ||
    description !== place.description ||
    category !== place.category ||
    url !== (place.url ?? '');

  async function save(): Promise<void> {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      await store.editPlace(place.id, {
        title: title.trim(),
        description,
        category,
        url: url.trim() === '' ? null : url.trim(),
      });
    } catch (caught) {
      handleFailure(caught, "L enregistrement a echoue.");
    } finally {
      setSaving(false);
    }
  }

  async function vote(value: -1 | 1): Promise<void> {
    try {
      if (mine === value) await store.clearVote(place.id);
      else await store.setVote(place.id, value);
    } catch (caught) {
      handleFailure(caught, 'Le vote n a pas pu etre enregistre.');
    }
  }

  async function submitComment(): Promise<void> {
    const body = draftComment.trim();
    if (body === '' || sendingComment) return;
    setSendingComment(true);
    try {
      await store.postComment(place.id, body);
      setDraftComment('');
    } catch (caught) {
      handleFailure(caught, 'Le commentaire n a pas pu etre publie.');
    } finally {
      setSendingComment(false);
    }
  }

  async function pickFile(changeEvent: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = changeEvent.currentTarget.files?.[0];
    // On vide le champ tout de suite : sans cela, reselectionner le meme
    // fichier apres un echec ne declenche aucun evenement `change`.
    changeEvent.currentTarget.value = '';
    if (file === undefined) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Formats acceptes : JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Photo trop lourde : 8 Mo maximum.');
      return;
    }

    setError(null);
    setUploadRatio(0);
    try {
      await store.sendPhoto(place.id, file, setUploadRatio);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 413) setError('Photo trop lourde : 8 Mo maximum.');
      else if (caught instanceof ApiError && caught.status === 415) setError('Format de photo refuse.');
      else handleFailure(caught, "L envoi de la photo a echoue.");
    } finally {
      setUploadRatio(null);
    }
  }

  async function togglePromotion(): Promise<void> {
    try {
      if (isStep) await store.demote(place.id);
      else await store.promote(place.id);
    } catch (caught) {
      handleFailure(caught, "La promotion a echoue.");
    }
  }

  async function destroy(): Promise<void> {
    if (!window.confirm(`Supprimer « ${place.title} » et tout son contenu ?`)) return;
    try {
      await store.removePlace(place.id);
      onClose();
    } catch (caught) {
      handleFailure(caught, 'La suppression a echoue.');
    }
  }

  return (
    <section
      className="panel placepanel"
      style={{ '--card-color': categoryStyle(category).color } as CSSProperties}
    >
      <header className="panel__head placepanel__head">
        <span className="placepanel__chip" title={categoryStyle(category).label}>
          {categoryStyle(category).glyph}
        </span>
        <span className="placepanel__heading">
          <h2 className="panel__title">{place.title}</h2>
          <span className="placepanel__coords">
            {place.lat.toFixed(4)}, {place.lng.toFixed(4)}
          </span>
        </span>
        <button className="button button--ghost button--icon" type="button" onClick={onClose} aria-label="Fermer la fiche">
          ✕
        </button>
      </header>

      <div className="panel__body">
        {isStep ? (
          <p className="placepanel__badge">
            <span aria-hidden="true">✦</span> Etape retenue de l itineraire
          </p>
        ) : null}

        <p className="placepanel__meta">
          Propose par <b>{author?.pseudo ?? 'quelqu un'}</b> le {formatClock(place.createdAt)}
        </p>

        <div className="votes">
          <button
            className={`button button--vote button--vote-up${mine === 1 ? ' is-active' : ''}`}
            type="button"
            onClick={() => void vote(1)}
          >
            Pour
          </button>
          <span className={`votes__count votes__count--${scoreTone(score)}`}>
            {scoreLabel(score)}
            <small>score</small>
          </span>
          <button
            className={`button button--vote button--vote-down${mine === -1 ? ' is-active' : ''}`}
            type="button"
            onClick={() => void vote(-1)}
          >
            Contre
          </button>
        </div>

        <h3 className="panel__section">La fiche</h3>

        <label className="field">
          <span className="field__label">Titre</span>
          <input
            className="field__input"
            type="text"
            maxLength={120}
            value={title}
            onChange={(inputEvent) => setTitle(inputEvent.currentTarget.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Description</span>
          <textarea
            className="field__input field__input--area"
            rows={4}
            maxLength={2000}
            value={description}
            onChange={(inputEvent) => setDescription(inputEvent.currentTarget.value)}
          />
        </label>

        <div className="field__row">
          <label className="field">
            <span className="field__label">Categorie</span>
            <select
              className="field__input"
              value={category}
              onChange={(selectEvent) => setCategory(selectEvent.currentTarget.value as Category)}
            >
              {CATEGORIES.map((entry) => (
                <option key={entry} value={entry}>
                  {categoryStyle(entry).label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Lien</span>
            <input
              className="field__input"
              type="url"
              placeholder="https://"
              value={url}
              onChange={(inputEvent) => setUrl(inputEvent.currentTarget.value)}
            />
          </label>
        </div>

        {place.url !== null ? (
          <p className="placepanel__link">
            <a href={place.url} target="_blank" rel="noreferrer noopener">
              Ouvrir le lien <span aria-hidden="true">↗</span>
            </a>
          </p>
        ) : null}

        {error !== null ? (
          <p className="panel__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="placepanel__actions">
          <button
            className="button button--primary"
            type="button"
            disabled={!dirty || saving}
            onClick={() => void save()}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
          <button className="button" type="button" onClick={() => void togglePromotion()}>
            {isStep ? 'Retirer de l itineraire' : 'Promouvoir dans l itineraire'}
          </button>
          <button className="button button--danger" type="button" onClick={() => void destroy()}>
            Supprimer
          </button>
        </div>

        <h3 className="panel__section">
          Photos <span className="panel__section-count">{photos.length}</span>
        </h3>

        <div className="gallery">
          {photos.map((photo) => (
            <figure className="gallery__item" key={photo.id}>
              <a href={photoUrl(photo.filename)} target="_blank" rel="noreferrer noopener">
                <img src={thumbUrl(photo.filename)} alt="" loading="lazy" width={160} height={120} />
              </a>
              {photo.memberId === me?.id ? (
                <button
                  className="button button--ghost gallery__remove"
                  type="button"
                  aria-label="Retirer cette photo"
                  onClick={() => void store.removePhoto(photo.id, place.id).catch(() => setError('Suppression impossible.'))}
                >
                  ✕
                </button>
              ) : null}
            </figure>
          ))}
          {photos.length === 0 && uploadRatio === null ? (
            <p className="gallery__empty">Aucune photo. Une vue vaut mieux qu un long debat.</p>
          ) : null}
        </div>

        {uploadRatio !== null ? (
          <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(uploadRatio * 100)}>
            <div className="progress__bar" style={{ width: `${Math.round(uploadRatio * 100)}%` }} />
            <span className="progress__label">{Math.round(uploadRatio * 100)} %</span>
          </div>
        ) : null}

        <input
          ref={fileInput}
          className="visually-hidden"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(changeEvent) => void pickFile(changeEvent)}
        />
        <button
          className="button button--block"
          type="button"
          disabled={uploadRatio !== null}
          onClick={() => fileInput.current?.click()}
        >
          {uploadRatio === null ? 'Ajouter une photo' : 'Envoi en cours...'}
        </button>

        <h3 className="panel__section">
          Commentaires <span className="panel__section-count">{comments.length}</span>
        </h3>

        <ul className="comments">
          {comments.map((comment) => {
            const speaker = memberById(state, comment.memberId);
            return (
              <li className="comment" key={comment.id}>
                <p className="comment__head">
                  <span
                    className="comment__avatar"
                    aria-hidden="true"
                    style={{ background: speaker?.color ?? 'var(--zero)' }}
                  >
                    {(speaker?.pseudo ?? '?').slice(0, 1).toUpperCase()}
                  </span>
                  <span className="comment__who">{speaker?.pseudo ?? 'quelqu un'}</span>
                  <span className="comment__when">{formatClock(comment.createdAt)}</span>
                  {comment.memberId === me?.id ? (
                    <button
                      className="button button--ghost button--icon"
                      type="button"
                      aria-label="Retirer ce commentaire"
                      onClick={() =>
                        void store
                          .removeComment(comment.id, place.id)
                          .catch(() => setError('Suppression impossible.'))
                      }
                    >
                      ✕
                    </button>
                  ) : null}
                </p>
                <p className="comment__body">{comment.body}</p>
              </li>
            );
          })}
          {comments.length === 0 ? (
            <li className="comments__empty">Personne n a encore reagi. Ouvre le debat.</li>
          ) : null}
        </ul>

        <div className="comments__compose">
          <textarea
            className="field__input field__input--area"
            rows={2}
            maxLength={2000}
            placeholder="Ecrire un commentaire"
            value={draftComment}
            onChange={(inputEvent) => setDraftComment(inputEvent.currentTarget.value)}
          />
          <button
            className="button button--primary"
            type="button"
            disabled={draftComment.trim() === '' || sendingComment}
            onClick={() => void submitComment()}
          >
            {sendingComment ? 'Envoi...' : 'Publier'}
          </button>
        </div>
      </div>
    </section>
  );
}
